import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Portal from "components/Portal";
import useAppSelector from "hooks/useAppSelector";
import PictureDialog from "./PictureDialog/PictureDialog";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import { Picture, type PictureGroupByStudent } from "types/Pictures";
import ProfileAvatar from "components/__reusable/ProfileAvatar";
import PictureThumbnail from "components/__reusable/PictureThumbnail";

interface PublishPictureAdjustmentDialogProps {
  onClose: () => void;
  pictureGroupByStudent: PictureGroupByStudent | undefined;
}

const PublishPictureAdjustmentDialog: React.FC<
  PublishPictureAdjustmentDialogProps
> = ({ onClose, pictureGroupByStudent }) => {
  const { t } = useTranslation();
  const { pictures } = useAppSelector((state) => state.picture);

  const renderStudentName = (student: PictureGroupByStudent | undefined) => {
    if (!student) return "";
    return `${student.last_name} ${student.first_name}`;
  };

  const [selectedPicture, setSelectedPictue] = useState<Picture | undefined>();

  return (
    <Portal>
      <div className="fixed top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center z-[80]">
        <div className="w-[90%] lg:w-1/2 p-5 bg-white z-[81]">
          <div className="h-[80vh] flex flex-col space-y-3">
            <h2 className="text-2xl font-semibold text-center">
              {t("_unpublished_picture_class._picture_adjustment")}
            </h2>
            <div
              id="student-info"
              className="w-full sm:w-2/5 lg:w-1/3 flex items-center space-x-2 min-w-0"
            >
              <div className="relative flex-shrink-0">
                {pictureGroupByStudent?.id === -1 ? (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-lg font-bold text-gray-700">
                    全
                  </div>
                ) : (
                  <ProfileAvatar
                    pictureUrl={
                      pictureGroupByStudent?.profile_picture_for_admin || ""
                    }
                  />
                )}
                <div className="w-5 h-5 sm:w-6 sm:h-6 absolute -bottom-1 -right-1 rounded-full text-white text-xs bg-[#e6b941] flex justify-center items-center font-semibold">
                  {pictureGroupByStudent?.student_pictures.length || 0}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm sm:text-base break-words leading-tight">
                  {pictureGroupByStudent?.id === -1
                    ? "クラス全体"
                    : renderStudentName(pictureGroupByStudent)}
                </p>
              </div>
            </div>
            <div className="flex-1 h-full overflow-y-auto relative">
              <div className="w-full h-full absolute">
                {pictureGroupByStudent && (
                  <div className="grid grid-cols-3 gap-3">
                    {pictureGroupByStudent.student_pictures.map((picture) => (
                      <button
                        key={picture.picture.id}
                        type="button"
                        className="flex items-start overflow-hidden"
                        onClick={() =>
                          setSelectedPictue(
                            pictures.find((pic) => pic.id === picture.picture.id),
                          )
                        }
                      >
                        <PictureThumbnail
                          imageSrc={
                            pictures.find((pic) => pic.id === picture.picture.id)
                              ?.thumbnail_picture || ""
                          }
                          name="student"
                          rounded={false}
                          hoverBrighten={true}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/2 lg:w-1/6">
              <SecondaryActionButton
                label={t("_generic._close")}
                onClick={onClose}
              />
            </div>
          </div>
          {selectedPicture && (
            <PictureDialog
              picture={selectedPicture}
              onPrevious={null}
              onNext={null}
              onClose={() => setSelectedPictue(undefined)}
            />
          )}
        </div>
      </div>
    </Portal>
  );
};

export default PublishPictureAdjustmentDialog;
